import axios from "axios";
import MainLayout from "@/components/layout/admin/MainLayout.vue";
import Select2Multiple from 'v-select2-multiple-component';
import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import ValidationService from "@/services/ValidationService";
import { maxLength, minLength, numeric, required } from "vuelidate/lib/validators";

export default {
  name: "ParentAndStudentCreate",
  components: {
    MainLayout,
    Select2Multiple,
    flatPickr,
    Loading,
  },
  mixins: [ValidationService],
  data() {
    return {
      parentData: {
        guardian1_name: "",
        phone1: "",
        address: "",
      },
      studentData: {
        name: "",
        dob: "",
        student_no: "",
      },

      academicYearArr: [],
      gradeArr: [],
      sectionArr: [],

      selectAcdYear: null,
      selectedGrade: null,
      selectedSection: null,
      referredByStudentNo: "",
      referrerInfo: null,
      referrerLookupError: "",

      submitted: false,
      isLoading: false,
      disabled: false,

      config: {
        maxDate:
          new Date().getFullYear() +
          "-" +
          (new Date().getMonth() + 1) +
          "-" +
          new Date().getDate(),
      },
    };
  },

  validations: {
    parentData: {
      guardian1_name: {
        required,
        maxLength: maxLength(50),
      },
      phone1: {
        required,
        numeric,
        minLength: minLength(8),
        maxLength: maxLength(11),
      },
      address: {
        required,
        maxLength: maxLength(1000),
      },
    },
    studentData: {
      name: {
        required,
        maxLength: maxLength(100),
      },
      dob: {
        required,
      },
    },
  },

  watch: {
    selectAcdYear(acdYearId) {
      if (acdYearId) {
        this.selectedGrade = null;
        this.getGradeName(acdYearId);
        this.generateStudentNumber();
      }
    },

    async selectedGrade(gradeId) {
      if (gradeId && this.selectAcdYear) {
        this.selectedSection = null;
        this.sectionArr = [];
        await this.getSection(gradeId);
        if (this.sectionArr.length > 0) {
          this.selectedSection = this.sectionArr[0].id;
        }
      }
    },
  },

  methods: {
    lookupReferrer() {
      const studentNo = this.referredByStudentNo.trim();

      this.referrerInfo = null;
      this.referrerLookupError = "";

      if (!studentNo) {
        return;
      }

      axios
        .get(`student/getReferrerStudentName/${studentNo}`)
        .then((res) => {
          this.referrerInfo = res.data;
        })
        .catch((error) => {
          this.referrerInfo = null;
          this.referrerLookupError =
            error.response?.data?.message || "Unable to find referrer student.";
        });
    },

    getAcademicYears() {
      axios
        .get("period/periodList")
        .then((res) => {
          this.academicYearArr = res.data.data;
          if (this.academicYearArr.length > 0) {
            this.selectAcdYear = this.academicYearArr[0].id;
          } else {
            this.selectAcdYear = null;
          }
        })
        .catch((err) => {
          if (err.response && err.response.status == 401) {
            this.$store.commit("logout");
            window.location.replace("login");
          }
        })
        .finally(() => (this.isLoading = false));
    },

    getGradeName(acdYearId) {
      axios
        .get("getGradeNameByAcademicYear", {
          params: {
            id: acdYearId,
            campus_id: this.$store.state.user.campus_id,
          },
        })
        .then((res) => {
          this.gradeArr = res.data;
          if (this.gradeArr.length > 0) {
            this.selectedGrade = this.gradeArr[0].id;
          } else {
            this.selectedGrade = null;
          }
          this.isLoading = false;
        })
        .catch((error) => {
          if (error.response && error.response.status == 401) {
            this.$store.commit("logout");
            window.location.replace("login");
          }
          this.isLoading = false;
        });
    },

    getSection(gradeId) {
      return axios
        .get("/class/getSection/" + gradeId, {
          params: {
            campus_id: this.$store.state.user.campus_id,
            academic_year: this.selectAcdYear,
          },
        })
        .then((response) => {
          if (response.data[0] && response.data[0].length > 0) {
            this.sectionArr = response.data[0];
          } else {
            this.sectionArr = [];
            this.selectedSection = null;
          }
          this.isLoading = false;
        })
        .catch((error) => {
          if (error.response && error.response.status == 401) {
            this.$store.commit("logout");
            window.location.replace("login");
          }
          this.isLoading = false;
        });
    },

    async generateStudentNumber() {
      if (this.selectAcdYear) {
        try {
          const response = await axios.get(`/student/generate-student-number/${this.selectAcdYear}`);
          if (response.data && response.data.student_no) {
            this.studentData.student_no = response.data.student_no;
          }
        } catch (error) {
          console.error("Error generating student number:", error);
        }
      }
    },

    async confirmRegister() {
      if (this.disabled) return;
      this.submitted = true;

      if (this.$v.$invalid) {
        this.validationErrors = {};
        return;
      }

      this.clearValidationErrors();
      this.disabled = true;

      await this.register();
    },

    async register() {
      this.isLoading = true;

      try {
        const request = {
          // Parent Data
          guardian1_name: this.parentData.guardian1_name,
          phone1: this.parentData.phone1,
          address: this.parentData.address,

          // Student Data
          name: this.studentData.name,
          referred_by_student_no: this.referredByStudentNo,
          dob: this.studentData.dob,
          academic_year: this.selectAcdYear,
          grade: this.selectedGrade,
          section: this.selectedSection,
          campus_id: this.$store.state.user.campus_id,
          created_id: this.$store.state.user.id,
        };

        const response = await axios.post(
          "/student/parent-student-create",
          request,
        );

        if (response.data.success) {
          this.$notification.success(response.data.message, {
            timer: 3,
            position: "bottomCenter",
          });
          if (response.data.class_student_id) {
            this.$router.push({
              name: "enrollmentPaymentCreate",
              params: {
                classStudentId: response.data.class_student_id,
              },
            });
          } else {
            this.$router.push({ name: "parentList" });
          }
        } else {
          this.$notification.error(response.data.message, {
            timer: 3,
            position: "bottomCenter",
          });
          this.disabled = false;
        }
      } catch (error) {
        if (error.response) {
          this.printValidationError(error.response);
          if (error.response.status == 401) {
            this.$store.commit('logout');
            window.location.replace('login');
            this.$notification.error("Unable to create. Please try again.", {
              timer: 3,
              position: "bottomCenter",
            });
          }
          this.disabled = false;
        }
      } finally {
        this.isLoading = false;
      }
    },

    showList() {
      this.$router.push({ path: "/parent/list" });
    },
  },

  mounted() {
    this.isLoading = true;
    this.getAcademicYears();
  },
};
